import MapboxDraw from "@mapbox/mapbox-gl-draw";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import includes from "lodash/includes";
import isEqual from "lodash/isEqual";
import mapboxgl from "mapbox-gl";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { MapTheme } from "@biggeo/bg-server-lib/studio";
import { Routes } from "../../../navigation/redux/model";
import { useMapData } from "../../redux/hooks";
import {
    DirectSelectModeOverride,
    DrawCircleMode,
    DrawPolygonModeOverride,
    DrawSquareMode,
    SimpleSelectModeOverride,
} from "../../utils/draw-modes";
import { coordinatesGeocoder } from "../../utils/geometry-utils";
import { FunctionType } from "../../utils/type";

import {
    DEFAULT_MAP_STYLE,
    getMapStyleFromSprite,
} from "../../views/MapStyles";
import { onMapReset, useMap, useMapState } from "../context";
import { removeMap } from "../utils/map-utils";
import { useConflictAreas } from "./conflict-areas-hooks";
import { useData } from "./data-hooks";
import { usePolygonHook } from "./polygon-hooks";
import { usePopup } from "./popup-hooks";
import { useRadius } from "./radius-hooks";
import { useSelectMode } from "./select-hooks";
import { useSquare } from "./square-hooks";
import { usePolygonStyle } from "./style-hooks";
import { useViewport } from "./viewport-hooks";
import { getMultiFilters } from "../context/utils/general-utils";
import { useMapStateUtilities, usePolygon } from "../context/hooks";
import { useSavedPolygon } from "./saved-polygon-hooks";
import { getMapStyleSprite } from "@biggeo/bg-utils";

export const WORLD_VIEW_ZOOM_LEVEL = 1.17;

export type InitializeMapReturnType = {
    isMapLoaded: boolean;
    map: React.MutableRefObject<mapboxgl.Map | null>;
    draw: React.MutableRefObject<MapboxDraw | null>;
    changeProjection: (proj: "globe" | "mercator") => void;
    projection: "globe" | "mercator";
    zoomIn: () => void;
    zoomOut: () => void;
    selectMode: (mode: boolean) => void;
    isSelectMode: boolean;
    showAiPrompt: boolean;
    setShowAiPrompt: React.Dispatch<React.SetStateAction<boolean>>;
    isDrawMode: boolean;
};

export const useInitializeMap = (): InitializeMapReturnType => {
    const {
        map,
        draw,
        isMapLoaded,
        dispatch,
        mapState,
        mapStyle,
        datasets,
        functionType,
        polygons,
    } = useMap();

    const { setViewport, setFunctionType } = useMapStateUtilities();

    const { deletePolygon, overridePolygons } = usePolygon();

    const multiFilters = getMultiFilters();

    const savedView = useMapData();

    const style =
        isMapLoaded && map.current ? map.current.getStyle() : undefined;

    const geocode = useRef<MapboxGeocoder | null>(null);

    const [showAiPrompt, setShowAiPrompt] = useState(false);
    const [projection, setProjection] = useState<"globe" | "mercator">(
        "mercator"
    );

    const location = useLocation();
    const isSavedViewPage = location.search.includes(Routes.savedView);

    const isDrawMode =
        isEqual(functionType, FunctionType.polygon) ||
        isEqual(functionType, FunctionType.radius) ||
        isEqual(functionType, FunctionType.square);

    const { onShapeStyleLoad, styles, loadCustomShapeSources } =
        usePolygonStyle({
            map,
            isMapLoaded,
        });

    const { onStyleLoad } = useSavedPolygon({ isSavedViewPage, style });

    const { select, selectMode } = useSelectMode({
        draw,
        map,
        onShapeStyleLoad,
        isMapLoaded,
        isDrawMode,
    });

    const {
        onStyleLoad: onStyleLoadViewport,
        bounds,
        viewport: updatedViewport,
    } = useViewport();

    const { onConflictAreaStyleLoad, actions } = useConflictAreas({
        isSelectMode: select,
        deletePolygon,
    });
    usePopup({
        map,
        draw,
        isDrawMode,
        functionType,
        isMapLoaded,
        isSelectMode: select,
        actions,
        isSavedViewPage,
    });

    useRadius();

    usePolygonHook();

    useSquare();

    const { onStyleLoad: onStyleLoadData } = useData({
        map,
        multiFilters,
        style,
        isMapLoaded,
        filters: [],
        isSavedViewPage,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const initMapStyle = isSavedViewPage
            ? mapStyle
            : (getMapStyleFromSprite(mapState?.styleUrl) ?? MapTheme.light);

        if (!initMapStyle) return;

        if (!map.current) {
            const worldBounds: [mapboxgl.LngLatLike, mapboxgl.LngLatLike] = [
                [-180, -90],
                [180, 90],
            ];

            const previousMapBounds = mapState?.viewport?.bounds;

            const previousBounds:
                | [mapboxgl.LngLatLike, mapboxgl.LngLatLike]
                | undefined = previousMapBounds
                ? [
                      [previousMapBounds._sw.lng, previousMapBounds._sw.lat],
                      [previousMapBounds._ne.lng, previousMapBounds._ne.lat],
                  ]
                : undefined;

            const savedViewBounds:
                | [mapboxgl.LngLatLike, mapboxgl.LngLatLike]
                | undefined = savedView?.savedView.viewport
                ? [
                      [
                          savedView.savedView.viewport.lngBounds.min,
                          savedView.savedView.viewport.latBounds.min,
                      ],
                      [
                          savedView.savedView.viewport.lngBounds.max,
                          savedView.savedView.viewport.latBounds.max,
                      ],
                  ]
                : undefined;

            const bounds: [mapboxgl.LngLatLike, mapboxgl.LngLatLike] =
                savedViewBounds
                    ? savedViewBounds
                    : previousBounds
                      ? previousBounds
                      : worldBounds;

            map.current = new mapboxgl.Map({
                container: "map",
                style: initMapStyle
                    ? getMapStyleSprite(initMapStyle)
                    : undefined,
                doubleClickZoom: false,
                minZoom: 1,
                bounds: bounds,
                projection,
                refreshExpiredTiles: false,
                maxTileCacheSize: 10000000,
                renderWorldCopies: false,
                accessToken:
                    "pk.eyJ1IjoiY2NhcnRoIiwiYSI6ImNsb3E1NTlweDBkbHMyaW9iaXRrNTZ1ZmIifQ.J95n-vaI-pyGcO8Hd082xQ",
                preserveDrawingBuffer: true,
            });

            if (draw.current) {
                const hasDrawControl = map.current.hasControl(draw.current);

                if (!hasDrawControl) {
                    map.current.addControl(draw.current);
                }
            } else {
                draw.current = new MapboxDraw({
                    displayControlsDefault: false,
                    keybindings: true,
                    userProperties: true,
                    modes: {
                        ...MapboxDraw.modes,
                        draw_polygon: DrawPolygonModeOverride,
                        draw_square: DrawSquareMode,
                        draw_circle: DrawCircleMode,
                        direct_select: DirectSelectModeOverride,
                        simple_select: SimpleSelectModeOverride,
                    },
                    controls: {
                        trash: true,
                    },
                    styles,
                });

                map.current.addControl(draw.current);
            }

            geocode.current = new MapboxGeocoder({
                accessToken:
                    "pk.eyJ1IjoiY2NhcnRoIiwiYSI6ImNsb3E1NTlweDBkbHMyaW9iaXRrNTZ1ZmIifQ.J95n-vaI-pyGcO8Hd082xQ",
                mapboxgl: mapboxgl,
                localGeocoder: coordinatesGeocoder,
                zoom: 4,
                placeholder: "Search name or coordinates",
                reverseGeocode: true,
            });

            const geocoderElement = document.getElementById("geocoder");
            geocoderElement?.appendChild(geocode.current.onAdd(map.current));

            const isConfigSavedAreas =
                includes(location.search, "savedAreaId") &&
                includes(location.search, "mapTab");

            if (mapState && !isConfigSavedAreas && !isSavedViewPage) {
                onMapReset({
                    map: map.current,
                    draw: draw.current,
                    state: mapState,
                    polygons,
                    setState: {
                        setPolygons: (p) => {
                            overridePolygons(p);
                        },
                        setFunctionType: (f) => setFunctionType(f),
                        setMode: (m) => selectMode(m),
                        setViewport: (v) => {
                            if (v.view) {
                                setViewport(v.view);
                            }
                            if (v.center && map.current) {
                                map.current.setCenter(v.center);
                            }
                        },
                    },
                });
            }

            map.current.on("load", () => {
                dispatch?.({
                    type: "SET_MAP",
                    values: {
                        isMapLoaded: true,
                        draw,
                        map,
                    },
                });
            });

            map.current.on("style.load", () => {
                if (map.current) {
                    loadCustomShapeSources(map.current);

                    onStyleLoadData(map.current);
                    onStyleLoadViewport();
                    onStyleLoad(map.current);
                    onConflictAreaStyleLoad(map.current);

                    // remove mapbox default control container
                    const container = map.current.getContainer();
                    const controlContainer = container.querySelector(
                        ".mapboxgl-control-container"
                    );
                    if (controlContainer) controlContainer.remove();
                }
            });
        }

        return () => {
            removeMap({
                isSavedViewPage,
                geocode,
                map,
                draw,
                isMapLoaded,
                dispatch,
            });
        };
    }, [map, draw, mapStyle]);

    useMapState({
        isMapLoaded,
        state: {
            map,
            draw,
            datasets,
            polygons,
            modes: {
                draw: isDrawMode,
                select,
            },
            featureCollection: mapState?.featureCollection,
            functionType,
            viewport: {
                bounds,
                center: bounds ? bounds.getCenter() : undefined,
                view: updatedViewport,
                zoom:
                    isMapLoaded && map.current
                        ? map.current.getZoom()
                        : WORLD_VIEW_ZOOM_LEVEL,
            },
            styleUrl: style?.sprite ?? DEFAULT_MAP_STYLE.sprite,
        },
        dispatch,
        callbacks: {
            deletePolygon,
        },
    });

    const changeProjection = (proj: "globe" | "mercator") => {
        if (map.current) {
            map.current.setProjection(proj);
            setProjection(proj);
        }
    };

    const zoomIn = () => {
        if (map.current) map.current?.zoomIn();
    };

    const zoomOut = () => {
        if (map.current) map.current?.zoomOut();
    };

    return {
        isMapLoaded,
        map,
        draw,
        changeProjection,
        projection,
        zoomIn,
        zoomOut,
        selectMode,
        isSelectMode: select,
        showAiPrompt,
        setShowAiPrompt,
        isDrawMode,
    };
};
